import { get, has, filter, indexOf, flatten } from 'lodash';

import Config from 'config';

import features from 'lib/common/features';
import TessituraAPI from 'lib/tessitura/REST/tessitura-wrapper';
import fallbacks from 'lib/common/fallbacks';
import logger from 'lib/common/logger';
import { enhanceSeatData, setScreenZoneData } from 'lib/common/transforms';
import ScreenMap from 'lib/tessitura/REST/maps/Screen';
import SectionMap from 'lib/tessitura/REST/maps/Section';
import SeatMap from 'lib/tessitura/REST/maps/Seat';
import SeatStatusMap from 'lib/tessitura/REST/maps/SeatStatus';
import CartMap from 'lib/tessitura/REST/maps/Cart';
import NFSPackageDetail from 'lib/tessitura/REST/maps/NFSPackageDetail';
import PerformanceDetail from 'lib/tessitura/REST/maps/PerformanceDetail';
import ProductionDetails from 'lib/tessitura/REST/maps/ProductionDetail';
import ZoneMap from 'lib/tessitura/REST/maps/Zone';

const config = new Config().getConfig();

let API;
let INITIAL_SESSION_KEY;

export function configure(endpoint, initialSessionKey) {
  let apiInstance = new TessituraAPI(endpoint, initialSessionKey);
  API = apiInstance;

  if (initialSessionKey) {
    setInitialSessionKey(initialSessionKey);
  }
}

export function getInitialSessionKey() {
  return INITIAL_SESSION_KEY || '%sessionKey%';
}

export function setInitialSessionKey(sessionKey) {
  INITIAL_SESSION_KEY = sessionKey;
  return INITIAL_SESSION_KEY;
}

export function getSessionKey() {
  let initialSessionKey = getInitialSessionKey();
  if (initialSessionKey) {
    return Promise.resolve(initialSessionKey);
  } else {
    return API.getSessionKey();
  }
}

export function loginInfo() {
  return API.requestGet({
    method: `Web/Session/${getInitialSessionKey()}`
  }).then(response => {
    if (has(response, 'result.ModeOfSaleId')) {
      return {
        MOS: response.result.ModeOfSaleId,
        promotion_code: response.result.SourceId
      };
    } else {
      logger.captureMessage('Failed to load LoginInfoEx');
      return false;
    }
  });
}

export function StoreADAData(data) {
  return API.requestPut({
    method: `Web/Cart/%sessionKey%/Properties`,
    params: {
      Notes: data
    }
  });
}

export function getPromoCode(promoCode, sourceCode) {
  return API.request({
    method: `Web/Session/%sessionKey%/PromoCode`,
    params: {
      PromoCodeString: promoCode,
      PromoCode: sourceCode
    }
  });
}

export function removePromoCode() {
  // Update the source number and mode of sale, then refresh
  return API.requestPut({
    method: 'Web/Cart/%sessionKey%/Properties',
    params: {
      SourceId: features.getOption('promoCode', 'defaultSourceNo'),
      ModeOfSaleId: features.getOption('promoCode', 'defaultModeOfSaleId')
    }
  }).then(() => {
    window.location.reload();
    return true;
  });
}

export function addPromoCode(promoCode) {
  return getPromoCode(promoCode).then(response => {
    // Update the source number and mode of sale, then refresh

    return API.requestPut({
      method: 'Web/Cart/%sessionKey%/Properties',
      params: {
        SourceId: response.result[0].Source.Id,
        ModeOfSaleId: response.result[0].ModeOfSaleId
      }
    }).then(() => {
      window.location.reload();
      return true;
    });
  });
}

// @TODO: Decouple this method
export function getSeatmapData(
  performanceId,
  packageNo,
  facilityNo,
  currentMOS,
  sourceCode,
  usesGeneratedSeatmap
) {
  let promises = [];

  /**
   * By default GetSeatsBriefEx requires a session key, meaning each user
   * gets a unique cache hit, so the cache only works for subsequent calls.
   *
   * If cacheableGetSeats is enabled, assuming a GetSeatsBriefExMOS method,
   * we swap out the get seats call out with one that doesn't require
   * a session key; in place we need to pass MOS and source code.
   *
   * This allows us to cache the call in a non-unique way,
   * which greatly increases the value of the cache.
   */
  if (packageNo) {
    promises = promises.concat([
      // Custom properties, removes need for session key
      API.requestGet({
        method: `TXN/Packages/${packageNo}/Seats`,
        params: {
          packageId: packageNo,
          modeOfSaleId: currentMOS,
          constituentId: 0
        }
      })
    ]);
  } else {
    promises = promises.concat([
      // Custom properties, removes need for session key
      API.requestGet({
        method: `TXN/Performances/${performanceId}/Seats`,
        params: {
          performanceId: performanceId,
          modeOfSaleId: currentMOS,
          constituentId: 0
        }
      })
    ]);
  }

  promises = promises.concat([
    API.requestGet({
      method: 'ReferenceData/SeatStatuses',
    })
  ]);

  if (usesGeneratedSeatmap && !packageNo) {
    promises = promises.concat([
      getScreensForPerformance(facilityNo),
      getSectionsForPerformance(facilityNo)
    ]);
  }


  return Promise.all(promises)
    .then(function(responses) {
      let seatsBriefResults = get(responses[0], 'result');
      let seatStatusResults = get(responses[1], 'result');
      let screensResults = get(responses[2], 'result');
      let seatsWithZones = get(responses[3], 'result');

      let seatsMap = new SeatMap();
      let statusMap = new SeatStatusMap();
      let screenMap = new ScreenMap();
      let sectionMap = new SectionMap();

      let seatsResults = enhanceSeatData(
        seatsMap.map(seatsBriefResults, true),
        statusMap.map(seatStatusResults),
        sectionMap.map(seatsWithZones),
        screensResults === undefined
          ? screenMap.map()
          : screenMap.map(screensResults.Screens),
      );

      let screenZoneData =
        seatsBriefResults && setScreenZoneData(seatsMap.map(seatsBriefResults));

      return {
        screensResults: {
          Screens:
            screensResults === undefined
              ? screenMap.map()
              : screenMap.map(screensResults.Screens)
        },
        seatsStatusResults: seatStatusResults,
        seatsResults: seatsResults,
        screenZoneData: screenZoneData
      };
    })
    .catch(fallbacks.redirectWithError);
}

export function getScreensForPerformance(facilityNo) {
  return API.requestGet({
    method: `TXN/Facilities/${facilityNo}/Screens`,
  });
}

export function getSectionsForPerformance(facilityNo) {
  return API.requestGet({
    method: `TXN/Facilities/${facilityNo}`,
  }).then(response => {
    return API.requestGet({
      method: 'ReferenceData/Sections',
      params: {
        seatMapId: response.result.SeatMap.Id,
      },
    });
  });
}

export function getMaxTicketAmount() {
  if (config.customMaxTicketsLimit) {
    return Promise.resolve(config.customMaxTicketsLimit);
  } else {
    return Promise.resolve(10);
  }
}

export function getNFSPackageDetail(packageNo, performanceId, modeOfSale, sourceNo) {
  return API.requestGet({
    method: `TXN/Packages/${packageNo}/Details`,
    params: {
      modeOfSaleId: modeOfSale,
      sourceId: sourceNo,
    },
  }).then(packageDetails => {
    const performances = packageDetails.result.PerformanceGroupDetails.reduce((carry, group) => {
      return [].concat(carry, group.PerformanceGroupPerformanceDetails.map(perf => {
        return perf.PerformanceId;
      }));
    }, []);

    const promises = [
      Promise.resolve(packageDetails),

      API.requestGet({
        method: 'TXN/PerformancePackageModeOfSales',
        params: {
          modeOfSaleId: modeOfSale,
          packageIds: packageNo,
        },
      }),

      API.requestGet({
        method: `TXN/PriceTypes/Details`,
        params: {
          packageId: packageNo,
          performanceIds: performanceId,
          modeOfSaleId: modeOfSale,
          sourceId: sourceNo,
        },
      }),

      // STC has _too_ many performances to load in one go
      // there's functionality in the bottom to chunk this
      // API.requestGet({
      //   method: `TXN/Performances/Summary`,
      //   params: {
      //     performanceIds: performances.join(','),
      //   },
      // }),
    ];

    const size = 25;
    const performancesRequests = [];

    for (let i = 0; i < performances.length; i += size) {
      const chunk = performances.slice(i, i + size);

      performancesRequests.push(
        API.requestGet({
          method: `TXN/Performances/Summary`,
          params: {
            performanceIds: chunk.join(','),
          },
        }).then(response => {
          return response.result;
        })
      );
    }

    promises.push(
      Promise.all(performancesRequests).then(responses => {
        return {
          result: flatten(responses),
        }
      })
    );

    return Promise.all(promises).then(responses => {
      return NFSPackageDetail(responses);
    });
  });
}

export function performanceDetailWithDiscounting(performanceId, packageId, modeOfSale, sourceCode) {
  return API.requestGet({
    method: `TXN/Performances/${performanceId}`,
  }).then(performanceDetails => {
    let packagePriceTypes = Promise.resolve({
      result: [],
    });

    if (packageId) {
      packagePriceTypes = API.requestGet({
        method: `TXN/PriceTypes/Details`,
        params: {
          packageId: packageId,
          performanceIds: performanceId,
          modeOfSaleId: modeOfSale,
          sourceId: sourceCode,
        },
      });
    }

    const promises = [
      Promise.resolve(performanceDetails),

      API.requestGet({
        method: 'TXN/PerformancePackageModeOfSales',
        params: {
          modeOfSaleId: modeOfSale,
          performanceIds: performanceId,
        },
      }),

      API.requestGet({
        method: 'TXN/Performances/Prices',
        params: {
          performanceIds: performanceId,
          modeOfSaleId: modeOfSale,
          sourceId: sourceCode,
        },
      }),

      API.requestGet({
        method: 'TXN/PriceTypes/Details',
        params: {
          performanceIds: performanceId,
          modeOfSaleId: modeOfSale,
          sourceId: sourceCode,
        },
      }),

      packagePriceTypes,

      API.requestGet({
        method: 'TXN/Performances/ZoneAvailabilities',
        params: {
          performanceIds: performanceId,
        },
      }),

      API.requestGet({
        method: `TXN/WebContents`,
        params: {
          productionElementIds: performanceId,
        },
      }),
    ];

    return Promise.all(promises).then(responses => {
      return PerformanceDetail(responses);
    });
  });
}

export function getProductionDetail(productionSeasonNo) {
  return API.requestGet({
    method: `TXN/ProductionSeasons/${productionSeasonNo}`,
  }).then(productionResponse => {
    const promises = [
      Promise.resolve(productionResponse),

      API.requestGet({
        method: 'TXN/Performances',
        params: {
          productionSeasonId: productionSeasonNo,
        },
      }),
    ];

    return Promise.all(promises).then(responses => {
      return ProductionDetails(responses);
    });
  });
}

export function getCart() {
  return API.requestGet({
    method: 'Web/Cart/%sessionKey%',
    params: {}
  }).then(response => {
    return CartMap(response);
  });
}

export function ReserveTicketsSpecifiedSeats(performanceId, lineItems, fixedPackage) {
  const wheelChairSeats = filter(lineItems, item => {
    return (
      indexOf(config.specialRequests.excludeSeatType, parseInt(item.seatData.seat_type)) > -1 ||
      indexOf(config.specialRequests.excludeHoldCode, parseInt(item.seatData.hc_no)) > -1
    );
  });

  let leaveSingleSeats = config.specialRequests.LeaveSingleSeats ? config.specialRequests.LeaveSingleSeats : 'Y';

  if (wheelChairSeats.length) {
    leaveSingleSeats = 'Y';
  }

  if (fixedPackage) {
    return API.request({
      method: 'Web/Cart/%sessionKey%/Packages/Fixed',
      params: {
        // TODO: map these before passing through
        PriceType: lineItems.map(item => item.priceData.price_type).join(','),
        PackageId: fixedPackage,
        NumberOfSeats: lineItems.length,
        SpecialRequests: `LeaveSingleSeats=${leaveSingleSeats}`,
        RequestedSeats: lineItems.map(item => item.seatData.seat_no).join(','),
        ZoneId: 0,
      },
    }).then(response => {
      return {
        result: response.result.SeatsReserved
      };
    });
  }

  return API.request({
    method: 'Web/Cart/%sessionKey%/Tickets',
    params: {
      // TODO: map these before passing through
      PriceType: lineItems.map(item => item.priceData.price_type).join(','),
      PerformanceId: performanceId,
      NumberOfSeats: lineItems.length,
      SpecialRequests: `LeaveSingleSeats=${leaveSingleSeats}`,
      RequestedSeats: lineItems.map(item => item.seatData.seat_no).join(','),
      ZoneId: 0,
    },
  });
}

export function ReleaseTickets(iPerformanceNumber, iLineItemNumber, currentFixedPackage) {
  if (currentFixedPackage) {
    return API.requestDelete({
      method: `Web/Cart/%sessionKey%/Packages/Fixed/${currentFixedPackage}/${iLineItemNumber}`,
    });
  }

  return API.requestDelete({
    method: `Web/Cart/%sessionKey%/Tickets/${iPerformanceNumber}/${iLineItemNumber}`,
  });
}

export function AddPackageItemSeated(currentFixedPackage, lineItems) {
  var priceTypes = lineItems.map(item => item.priceData.price_type).join(',');
  var requestedSeats = lineItems.map(item => item.seatData.seat_no).join(',');

  return API.request({
    method: 'Web/Cart/%sessionKey%/Packages/Fixed',
    params: {
      // TODO: map these before passing through
      PriceType: priceTypes,
      PackageId: currentFixedPackage.pkg_no,
      NumberOfSeats: lineItems.length,
      SpecialRequests: 'LeaveSingleSeats=Y',
      RequestedSeats: requestedSeats,
      ZoneId: 0
    }
  }).then(response => {
    return {
      result: response.result.SeatsReserved
    };
  });
}

export function RemovePackageItem(pkgNo, liSeqNo) {
  return API.requestDelete({
    method: `Web/Cart/%sessionKey%/Packages/Fixed/${pkgNo}/${liSeqNo}`,
    params: {}
  });
}

export function AddNFSPackagePerformanceItem(packageLineItemNo, packageNo, performanceGroupNo, performanceNo, lineItems) {
  return API.request({
    method: 'Web/Cart/%sessionKey%/Packages/Nfs',
    params: {
      NFSPackageLineItemId: packageLineItemNo,
      PriceType: lineItems.map(item => item.priceData.price_type).join(','),
      PackageId: packageNo,
      PerformanceId: performanceNo,
      PerformanceGroupId: performanceGroupNo,
      NumberOfSeats: lineItems.length,
      ZoneId: 0,
      RequestedSeats: lineItems.map(item => item.seatData.seat_no).join(','),
      LeaveSingleSeats: true,
      SpecialRequests: '',
      Unseated: false,
    },
  });
}

export function RemoveNFSPackage(packageLineItemNo, performanceLineItemNo, performanceNo) {
  if (performanceLineItemNo && performanceNo) {
    return API.requestDelete({
      method: `Web/Cart/%sessionKey%/Packages/Nfs/${packageLineItemNo}/${performanceLineItemNo}/${performanceNo}`,
    });
  }

  return API.requestDelete({
    method: `Web/Cart/%sessionKey%/Packages/Nfs/${packageLineItemNo}`,
  });
}

export function getModeOfSaleOffers(modeOfSale, performanceId) {
  return API.requestGet({
    method: 'TXN/ModeOfSaleOffers',
    params: {
      modeOfSaleId: modeOfSale,
      performanceIds: performanceId
    }
  });
}

/**
 * Custom Method (GetCurrentPromoCode)
 */
export function GetCurrentPromoCode() {
  return API.requestGet({
    method: 'Web/Session/%sessionKey%'
  }).then(response => {
    return getPromoCode('', response.result.SourceId).then(response => {
      if (response.result.constructor === Array) {
        if (response.result[0] !== undefined) {
          response.result = response.result[0].PromoCode;
        } else {
          response.result = false;
        }
      }
      return response;
    });
  });
}

/**
 * Custom Method (SeatUpsells)
 */
export function SeatUpsells() {
  return API.requestGet({
    method: 'SeatUpsells',
    params: {}
  }).then(function(response) {
    let result = get(response, 'result.GetLocalDataResults.LocalData');
    return result;
  });
}

export default {
  configure,
  getInitialSessionKey,
  setInitialSessionKey,
  getSessionKey,
  loginInfo,
  getSeatmapData,
  getMaxTicketAmount,
  performanceDetailWithDiscounting,
  getNFSPackageDetail,
  getProductionDetail,
  getCart,
  ReserveTicketsSpecifiedSeats,
  ReleaseTickets,
  AddPackageItemSeated,
  RemovePackageItem,
  GetCurrentPromoCode,
  SeatUpsells,
  addPromoCode,
  removePromoCode,
  StoreADAData,
  AddNFSPackagePerformanceItem,
  RemoveNFSPackage,
};



// WEBPACK FOOTER //
// ./src/javascripts/lib/tessitura/REST/API.js